import { hasPermission, Permission } from 'core/auth/rbac'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  CatalogueIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  DashboardIcon,
  FarmerSalesIcon,
  GrievancesIcon,
  KYCVerificationIcon,
  OrdersIcon,
  PaymentsIcon,
  RupeeIcon,
  SettingsIcon,
  UserManagementIcon,
} from '../Icons'
import { SamarthLogo } from '../Logo/SamarthLogo'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export const Sidebar = ({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) => {
  const { user } = useAuth()

  const [shouldRenderMobile, setShouldRenderMobile] = useState(mobileOpen)
  const [isAnimatingMobile, setIsAnimatingMobile] = useState(false)

  useEffect(() => {
    if (mobileOpen) {
      setShouldRenderMobile(true)
      const timer = setTimeout(() => {
        setIsAnimatingMobile(true)
      }, 30)

      return () => clearTimeout(timer)
    } else {
      setIsAnimatingMobile(false)
      const timer = setTimeout(() => {
        setShouldRenderMobile(false)
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [mobileOpen])

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: DashboardIcon },
    {
      name: 'User Management',
      path: '/user-management',
      icon: UserManagementIcon,
      permission: 'view_user_management' as Permission,
    },
    {
      name: 'Partner KYC',
      path: '/kyc-verification',
      icon: KYCVerificationIcon,
      permission: 'view_kyc' as Permission,
    },
    { name: 'Catalogue', path: '/catalogue', icon: CatalogueIcon, permission: 'view_catalogue' as Permission },
    { name: 'Orders', path: '/orders', icon: OrdersIcon, permission: 'view_orders' as Permission },
    { name: 'Indents', path: '/indents', icon: RupeeIcon, permission: 'view_orders' as Permission },
    { name: 'Payments', path: '/payments', icon: PaymentsIcon, permission: 'view_payments' as Permission },
    { name: 'Grievances', path: '/grievances', icon: GrievancesIcon, permission: 'view_grievances' as Permission },
    {
      name: 'Farmer Sales',
      path: '/farmer-sales',
      icon: FarmerSalesIcon,
      permission: 'view_farmer_sales' as Permission,
    },
    { name: 'Settings', path: '/settings', icon: SettingsIcon, permission: 'view_settings' as Permission },
  ]

  const visibleMenuItems = menuItems.filter((item) => !item.permission || hasPermission(user, item.permission))

  return (
    <>
      {/* Desktop Sidebar (hidden on mobile, flex on desktop) */}
      <aside
        className={`hidden md:flex flex-col border-r border-border bg-surface text-text-primary transition-all duration-200 ${
          collapsed ? 'w-[72px]' : 'w-60'
        } h-screen flex-shrink-0`}
      >
        {/* Brand/Header section with Logo */}
        <div
          className={`flex items-center gap-2 py-5 transition-all duration-200 ease-linear ${collapsed ? 'justify-center px-4' : 'px-5'}`}
        >
          <SamarthLogo collapsed={collapsed} showText={!collapsed} size="small" />
        </div>

        {/* Menu items navigation list */}
        <nav className="flex flex-1 flex-col gap-1 px-3 overflow-y-auto">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-btn px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-linear ${
                    isActive ? 'text-primary' : 'text-text-secondary hover:bg-bg hover:text-text-primary'
                  } ${collapsed ? 'justify-center px-2' : ''}`
                }
                title={collapsed ? item.name : undefined}
              >
                <span className="flex-shrink-0">
                  <Icon size={20} />
                </span>
                {!collapsed && <span className="truncate">{item.name}</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* Bottom collapse trigger button */}
        <button
          onClick={onToggle}
          className="m-3 flex items-center justify-center gap-2 rounded-btn px-3 py-2 text-sm text-text-secondary hover:bg-bg transition-colors duration-150"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRightIcon size={16} /> : <ChevronLeftIcon size={16} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </aside>

      {/* Mobile Drawer (visible only on mobile viewports when shouldRenderMobile is true) */}
      {shouldRenderMobile && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop overlay */}
          <button
            className={`absolute inset-0 bg-black/40 backdrop-blur-sm cursor-default w-full h-full border-none focus:outline-none transition-opacity duration-300 ${
              isAnimatingMobile ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={onMobileClose}
            aria-label="Close menu"
          />

          {/* Drawer menu panel */}
          <aside
            className={`relative z-10 flex h-full w-64 flex-col bg-surface border-r border-border text-text-primary shadow-card transition-transform duration-300 ease-in-out ${
              isAnimatingMobile ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Header section with Logo & Close button */}
            <div className="flex items-center justify-between px-5 py-5 border-b border-border">
              <SamarthLogo collapsed={false} showText={true} size="small" />
              <button
                onClick={onMobileClose}
                className="rounded-btn p-1.5 hover:bg-bg text-text-secondary hover:text-text-primary transition-colors focus:outline-none"
                title="Close menu"
              >
                <CloseIcon size={20} />
              </button>
            </div>

            {/* Menu items navigation list */}
            <nav className="flex flex-1 flex-col gap-1 px-3 py-4 overflow-y-auto">
              {visibleMenuItems.map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-btn px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive ? 'text-primary' : 'text-text-secondary hover:bg-bg hover:text-text-primary'
                      }`
                    }
                  >
                    <span className="flex-shrink-0">
                      <Icon size={20} />
                    </span>
                    <span className="truncate">{item.name}</span>
                  </NavLink>
                )
              })}
            </nav>
          </aside>
        </div>
      )}
    </>
  )
}
